import { useEffect, useMemo, useRef, useState } from "react";
import html2pdf from "html2pdf.js";
import { toast } from "sonner";
import {
  Printer,
  Download,
  RotateCcw,
  FileText,
  Upload,
  Save,
  Settings2,
  MonitorDown,
  History,
  Database,
} from "lucide-react";
import ControlPanel from "@/components/invoice/ControlPanel";
import InvoicePreview from "@/components/invoice/InvoicePreview";
import SetupWizard from "@/components/invoice/SetupWizard";
import SavedInvoicesList from "@/components/invoice/SavedInvoicesList";
import {
  calcTotals,
  defaultInvoiceNumber,
  emptyLineItem,
  todayISO,
} from "@/lib/invoiceUtils";
import {
  isInstallPromptAvailable,
  isStandaloneMode,
  triggerInstallPrompt,
} from "@/lib/pwa";
import * as db from "@/lib/db";
import {
  supportsFileSystemAccess,
  pickNewDbFile,
  pickExistingDbFile,
  loadStoredHandle,
  clearStoredHandle,
  readBlob,
  writeBlob,
  downloadBlob,
  uploadBlob,
  getHandleName,
} from "@/lib/fs";
import DbOnboarding from "@/components/invoice/DbOnboarding";
import InstallGuide from "@/components/invoice/InstallGuide";

const blankBusiness = {
  name: "",
  address: "",
  phone: "",
  email: "",
  gstin: "",
  logoDataUrl: "",
  tagline: "",
  defaultUnit: "PCS",
  notes: "",
  paymentDetails: "",
  terms: "",
};

const blankCustomer = {
  name: "",
  address: "",
  phone: "",
  gstin: "",
};

const blankShipping = {
  enabled: false,
  sameAsBilling: true,
  name: "",
  address: "",
  phone: "",
  gstin: "",
};

const blankEway = {
  enabled: false,
  number: "",
  date: "",
  vehicle: "",
  transporter: "",
  placeOfSupply: "",
};

function buildBusinessFromConfig(cfg) {
  return {
    name: cfg?.name || "",
    address: cfg?.address || "",
    phone: cfg?.phone || "",
    email: cfg?.email || "",
    gstin: cfg?.gstin || "",
    logoDataUrl: cfg?.logoDataUrl || "",
    tagline: cfg?.tagline || "",
    defaultUnit: cfg?.defaultUnit || cfg?.defaults?.unit || "PCS",
    notes: cfg?.notes ?? cfg?.defaults?.notes ?? "",
    paymentDetails: cfg?.paymentDetails || "",
    terms: cfg?.terms ?? cfg?.defaults?.terms ?? "",
  };
}

// Normalise a party to {name, billTo:{phone,gstin,address}, shipTo?: {...}}
// Backward-compat: older format had phone/gstin/address at top level.
function normaliseParty(p) {
  if (!p) return null;
  const billTo = p.billTo
    ? {
        phone: p.billTo.phone || "",
        gstin: p.billTo.gstin || "",
        address: p.billTo.address || "",
      }
    : {
        phone: p.phone || "",
        gstin: p.gstin || "",
        address: p.address || "",
      };
  const out = { name: p.name || "", billTo };
  if (p.shipTo) {
    out.shipTo = {
      name: p.shipTo.name || "",
      phone: p.shipTo.phone || "",
      gstin: p.shipTo.gstin || "",
      address: p.shipTo.address || "",
    };
  }
  return out;
}

export default function InvoiceApp() {
  const [business, setBusiness] = useState(blankBusiness);
  const [customer, setCustomer] = useState(blankCustomer);
  const [shipping, setShipping] = useState(blankShipping);
  const [eway, setEway] = useState(blankEway);
  const [meta, setMeta] = useState({
    id: crypto.randomUUID(),
    number: defaultInvoiceNumber(),
    date: todayISO(),
    dueDate: "",
  });
  const [items, setItems] = useState([emptyLineItem()]);
  const [taxRate, setTaxRate] = useState(18);
  const [taxLabel, setTaxLabel] = useState("GST");
  const [parties, setParties] = useState([]);
  const [setupOpen, setSetupOpen] = useState(false);
  const [savedInvoices, setSavedInvoices] = useState([]);
  const [invoicesListOpen, setInvoicesListOpen] = useState(false);
  const firstRunChecked = useRef(false);
  const [installable, setInstallable] = useState(isInstallPromptAvailable());
  const [installed, setInstalled] = useState(isStandaloneMode());

  // SQLite + file-system state
  const [dbReady, setDbReady] = useState(false);
  const [dbName, setDbName] = useState("");
  const [dbHandle, setDbHandle] = useState(null);
  const [dbDirty, setDbDirty] = useState(false);
  const [dbOnboardingOpen, setDbOnboardingOpen] = useState(false);
  const [installGuideOpen, setInstallGuideOpen] = useState(false);
  const persistTimer = useRef(null);

  // PWA install prompt listeners
  useEffect(() => {
    const onInstallable = () => setInstallable(true);
    const onInstalled = () => {
      setInstallable(false);
      setInstalled(true);
    };
    window.addEventListener("pwa:installable", onInstallable);
    window.addEventListener("pwa:installed", onInstalled);
    return () => {
      window.removeEventListener("pwa:installable", onInstallable);
      window.removeEventListener("pwa:installed", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    const res = await triggerInstallPrompt();
    if (res.outcome === "accepted") {
      toast.success("App installed");
    } else if (res.outcome === "dismissed") {
      toast("Install dismissed");
    } else if (res.outcome === "unavailable") {
      // Open the install guide dialog with browser-specific manual steps.
      setInstallGuideOpen(true);
    }
  };

  // ----- Database lifecycle: open / persist / sync state -----

  const refreshAllFromDb = () => {
    const company = db.getCompany();
    if (company) {
      setBusiness(buildBusinessFromConfig(company));
      if (typeof company.taxRate === "number") setTaxRate(company.taxRate);
      if (company.taxLabel) setTaxLabel(company.taxLabel);
    }
    setParties(db.listParties());
    setSavedInvoices(db.listInvoices());
  };

  // On mount: try restoring the previously chosen .db file (Chrome/Edge desktop).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const handle = await loadStoredHandle();
        if (cancelled) return;
        if (handle) {
          try {
            const bytes = await readBlob(handle);
            await db.openFromBytes(bytes && bytes.length ? bytes : null);
            setDbHandle(handle);
            setDbName(await getHandleName(handle));
            setDbReady(true);
            refreshAllFromDb();
            toast("Database connected", {
              description: handle.name || "ezbiz.db",
            });
            // First-run setup if no company name yet
            setTimeout(() => {
              if (!firstRunChecked.current) {
                firstRunChecked.current = true;
                setBusiness((curr) => {
                  if (!curr.name?.trim()) setSetupOpen(true);
                  return curr;
                });
              }
            }, 200);
            return;
          } catch (e) {
            console.warn("Failed to read stored .db file:", e);
          }
        }
        // No usable handle — open the onboarding modal.
        setDbOnboardingOpen(true);
      } catch (e) {
        console.warn(e);
        setDbOnboardingOpen(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist DB to disk (debounced). Auto-save when File System Access is
  // available and a handle is connected; otherwise mark "dirty" for manual save.
  const schedulePersist = () => {
    setDbDirty(true);
    if (!dbHandle) return; // no handle -> manual Save will export
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(async () => {
      try {
        const bytes = db.exportBytes();
        if (bytes) await writeBlob(dbHandle, bytes);
        setDbDirty(false);
      } catch (e) {
        console.error("DB persist failed:", e);
        toast.error("Could not save database file");
      }
    }, 300);
  };

  const handleCreateNewDb = async () => {
    try {
      let handle = null;
      if (supportsFileSystemAccess) {
        handle = await pickNewDbFile();
        if (!handle) return;
      }
      await db.newEmptyDb();
      if (handle) {
        const bytes = db.exportBytes();
        if (bytes) await writeBlob(handle, bytes);
        setDbHandle(handle);
        setDbName(await getHandleName(handle));
      } else {
        setDbHandle(null);
        setDbName("(unsaved)");
      }
      setDbReady(true);
      setDbDirty(false);
      setDbOnboardingOpen(false);
      refreshAllFromDb();
      // Open Setup wizard so the user fills in company details immediately.
      setTimeout(() => setSetupOpen(true), 250);
      toast.success("New database created", {
        description: handle?.name || "Use Save to export when ready",
      });
    } catch (e) {
      if (e?.name !== "AbortError") {
        console.error(e);
        toast.error("Could not create database");
      }
    }
  };

  const handleOpenExistingDb = async () => {
    try {
      let bytes = null;
      let handle = null;
      let name = "";
      if (supportsFileSystemAccess) {
        handle = await pickExistingDbFile();
        if (!handle) return;
        bytes = await readBlob(handle);
        name = await getHandleName(handle);
      } else {
        const picked = await uploadBlob();
        if (!picked) return;
        bytes = picked.bytes;
        name = picked.name;
      }
      await db.openFromBytes(bytes && bytes.length ? bytes : null);
      setDbHandle(handle);
      setDbName(name || "(uploaded)");
      setDbReady(true);
      setDbDirty(false);
      setDbOnboardingOpen(false);
      refreshAllFromDb();
      toast.success("Database opened", { description: name });
    } catch (e) {
      if (e?.name !== "AbortError") {
        console.error(e);
        toast.error("Could not open database");
      }
    }
  };

  const handleContinueWithoutFile = async () => {
    await db.newEmptyDb();
    setDbHandle(null);
    setDbName("(unsaved)");
    setDbReady(true);
    setDbDirty(false);
    setDbOnboardingOpen(false);
    refreshAllFromDb();
    setTimeout(() => setSetupOpen(true), 200);
    toast("Working without a file", {
      description: "Click Save to download your data when done.",
    });
  };

  const handleSaveDbFallback = async () => {
    try {
      const bytes = db.exportBytes();
      if (!bytes) return;
      if (dbHandle) {
        await writeBlob(dbHandle, bytes);
        setDbDirty(false);
        toast.success("Saved", { description: dbName });
      } else {
        downloadBlob(bytes, "ezbiz.db");
        setDbDirty(false);
        toast.success("Database downloaded", { description: "ezbiz.db" });
      }
    } catch (e) {
      console.error(e);
      toast.error("Save failed");
    }
  };

  const handleDisconnectDb = async () => {
    await clearStoredHandle();
    db.close();
    setDbHandle(null);
    setDbName("");
    setDbReady(false);
    setDbDirty(false);
    setBusiness(blankBusiness);
    setParties([]);
    setSavedInvoices([]);
    setDbOnboardingOpen(true);
    toast("Disconnected from database");
  };

  // Parties: now sourced from SQLite. Mutations write through the DB and trigger persist.
  const persistParties = (list) => {
    db.replaceAllParties(list);
    setParties(db.listParties());
    schedulePersist();
  };

  const handleSelectParty = (index) => {
    const party = parties[index];
    if (!party) return;
    const billTo = party.billTo || {};
    setCustomer({
      name: party.name || "",
      address: billTo.address || "",
      phone: billTo.phone || "",
      gstin: billTo.gstin || "",
    });
    if (party.shipTo) {
      setShipping({
        enabled: true,
        sameAsBilling: false,
        name: party.shipTo.name || party.name || "",
        address: party.shipTo.address || "",
        phone: party.shipTo.phone || "",
        gstin: party.shipTo.gstin || "",
      });
    } else {
      setShipping({ ...blankShipping });
    }
    toast(`Loaded: ${party.name || "(unnamed)"}`);
  };

  const handleSaveParty = () => {
    if (!customer.name?.trim()) {
      toast.error("Add a customer name first");
      return;
    }
    const newParty = {
      name: customer.name.trim(),
      billTo: {
        phone: customer.phone || "",
        gstin: customer.gstin || "",
        address: customer.address || "",
      },
      ...(shipping?.enabled && !shipping.sameAsBilling
        ? {
            shipTo: {
              name: shipping.name || customer.name,
              phone: shipping.phone || "",
              gstin: shipping.gstin || "",
              address: shipping.address || "",
            },
          }
        : {}),
    };
    // Replace if a party with same name+gstin exists, otherwise append.
    const idx = parties.findIndex(
      (p) =>
        (p.name || "").trim().toLowerCase() === newParty.name.toLowerCase() &&
        (p.billTo?.gstin || "").trim().toUpperCase() ===
          (newParty.billTo.gstin || "").trim().toUpperCase(),
    );
    const next =
      idx >= 0
        ? parties.map((p, i) => (i === idx ? newParty : p))
        : [...parties, newParty];
    persistParties(next);
    toast.success(idx >= 0 ? "Party updated" : "Party added", {
      description: `${next.length} parties in book`,
    });
  };

  const handleRemoveParty = (index) => {
    const removed = parties[index];
    persistParties(parties.filter((_, i) => i !== index));
    toast(`Removed: ${removed?.name || "(unnamed)"}`);
  };

  const handleImportParties = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        const list = Array.isArray(data?.parties)
          ? data.parties
          : Array.isArray(data)
            ? data
            : null;
        if (!list) {
          toast.error("JSON must have a 'parties' array");
          return;
        }
        const normalised = list.map(normaliseParty).filter(Boolean);
        persistParties(normalised);
        toast.success("Parties imported", {
          description: `${normalised.length} parties loaded`,
        });
      } catch (e) {
        toast.error("Invalid JSON file");
        console.error(e);
      }
    };
    reader.readAsText(file);
  };

  const handleExportParties = () => {
    const blob = new Blob([JSON.stringify({ parties }, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "parties.json";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Parties exported", {
      description: `${parties.length} parties · parties.json`,
    });
  };

  const totals = useMemo(() => calcTotals(items, taxRate), [items, taxRate]);
  const previewRef = useRef(null);

  const effectiveShipping = !shipping.enabled
    ? { ...shipping, enabled: false }
    : shipping.sameAsBilling
      ? {
          ...shipping,
          name: customer.name,
          address: customer.address,
          phone: customer.phone,
          gstin: customer.gstin,
        }
      : shipping;

  const handlePrint = () => {
    window.print();
  };

  const handleImportConfig = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const cfg = JSON.parse(ev.target.result);
        const merged = buildBusinessFromConfig(cfg);
        setBusiness(merged);
        if (cfg?.defaults) {
          if (typeof cfg.defaults.taxRate === "number")
            setTaxRate(cfg.defaults.taxRate);
          if (cfg.defaults.taxLabel) setTaxLabel(cfg.defaults.taxLabel);
        }
        // Persist into the SQLite DB (and to file via debounced save).
        db.saveCompany({
          ...merged,
          taxLabel: cfg?.defaults?.taxLabel || taxLabel,
          taxRate:
            typeof cfg?.defaults?.taxRate === "number"
              ? cfg.defaults.taxRate
              : taxRate,
        });
        schedulePersist();
        toast.success("Config imported", { description: file.name });
      } catch (e) {
        toast.error("Invalid JSON file");
        console.error(e);
      }
    };
    reader.readAsText(file);
  };

  const handleExportConfig = () => {
    const cfg = {
      ...business,
      defaults: {
        taxLabel,
        taxRate,
      },
    };
    const blob = new Blob([JSON.stringify(cfg, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "company-config.json";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Config exported", { description: "company-config.json" });
  };

  const handleSetupSave = (draft, downloadFile) => {
    const next = {
      name: draft.name || "",
      address: draft.address || "",
      phone: draft.phone || "",
      email: draft.email || "",
      gstin: draft.gstin || "",
      logoDataUrl: draft.logoDataUrl || "",
      tagline: draft.tagline || "",
      defaultUnit: draft.defaultUnit || "PCS",
      notes: draft.notes || "",
      paymentDetails: draft.paymentDetails || "",
      terms: draft.terms || "",
    };
    setBusiness(next);
    if (draft.taxLabel) setTaxLabel(draft.taxLabel);
    if (typeof draft.taxRate === "number") setTaxRate(draft.taxRate);
    db.saveCompany({
      ...next,
      taxLabel: draft.taxLabel || taxLabel,
      taxRate:
        typeof draft.taxRate === "number" ? draft.taxRate : taxRate,
    });
    schedulePersist();
    toast.success("Setup saved", {
      description: dbHandle
        ? `Stored in ${dbName}`
        : "In-memory · click Save to write the .db file",
    });
    if (downloadFile) {
      const cfg = {
        ...next,
        defaults: {
          taxLabel: draft.taxLabel || taxLabel,
          taxRate:
            typeof draft.taxRate === "number" ? draft.taxRate : taxRate,
        },
      };
      const blob = new Blob([JSON.stringify(cfg, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "company-config.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }
  };

  // ---- Saved invoices (now backed by SQLite) ----
  const handleSaveInvoice = () => {
    if (!customer.name?.trim()) {
      toast.error("Add a customer name first");
      return;
    }
    const snapshot = {
      id: meta.id || crypto.randomUUID(),
      meta: { ...meta },
      customer: { ...customer },
      shipping: { ...shipping },
      eway: { ...eway },
      items: items.map((it) => ({ ...it })),
      taxRate,
      taxLabel,
      totals: { ...totals },
    };
    const stamped = db.upsertInvoice(snapshot);
    setSavedInvoices(db.listInvoices());
    setMeta((m) => ({ ...m, id: stamped.id }));
    schedulePersist();
    toast.success("Invoice saved", {
      description: `${stamped.meta?.number || ""}`,
    });
  };

  const handleLoadInvoice = (inv) => {
    if (!inv) return;
    setMeta({
      id: inv.id,
      number: inv.meta?.number || defaultInvoiceNumber(),
      date: inv.meta?.date || todayISO(),
      dueDate: inv.meta?.dueDate || "",
    });
    setCustomer({ ...blankCustomer, ...(inv.customer || {}) });
    setShipping({ ...blankShipping, ...(inv.shipping || {}) });
    setEway({ ...blankEway, ...(inv.eway || {}) });
    const loadedItems =
      Array.isArray(inv.items) && inv.items.length
        ? inv.items.map((it) => ({
            ...emptyLineItem(),
            ...it,
            id: it.id || crypto.randomUUID(),
          }))
        : [emptyLineItem()];
    setItems(loadedItems);
    if (typeof inv.taxRate === "number") setTaxRate(inv.taxRate);
    if (inv.taxLabel) setTaxLabel(inv.taxLabel);
    toast(`Loaded ${inv.meta?.number || "invoice"}`);
  };

  const handleDeleteInvoice = (id) => {
    db.deleteInvoiceById(id);
    setSavedInvoices(db.listInvoices());
    schedulePersist();
    toast("Invoice deleted");
  };

  const lastInvoiceNumber = useMemo(() => {
    if (!savedInvoices.length) return null;
    const sorted = [...savedInvoices].sort((a, b) =>
      (b.updatedAt || "").localeCompare(a.updatedAt || ""),
    );
    return sorted[0]?.meta?.number || null;
  }, [savedInvoices]);

  const handleDownloadPDF = async () => {
    if (!previewRef.current) return;
    const fileName = `${meta.number || "invoice"}.pdf`;
    toast("Generating PDF…", { description: fileName });
    const opt = {
      margin: 0,
      filename: fileName,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };
    try {
      await html2pdf().set(opt).from(previewRef.current).save();
      toast.success("PDF downloaded", { description: fileName });
    } catch (err) {
      toast.error("Failed to generate PDF");
      console.error(err);
    }
  };

  const handleReset = () => {
    setCustomer(blankCustomer);
    setShipping(blankShipping);
    setEway(blankEway);
    setItems([{ ...emptyLineItem(), unit: business.defaultUnit || "PCS" }]);
    setMeta({
      id: crypto.randomUUID(),
      number: defaultInvoiceNumber(),
      date: todayISO(),
      dueDate: "",
    });
    toast("Cleared", { description: "Invoice cleared. Company kept." });
  };

  return (
    <div className="app-shell w-full min-h-screen flex flex-col lg:flex-row bg-[#FAFAFA]">
      {/* LEFT — Controls */}
      <aside
        className="no-print w-full lg:w-[46%] lg:h-screen lg:overflow-y-auto scroll-clean border-r border-[#E5E5E5] bg-white"
        data-testid="control-panel"
      >
        <header className="sticky top-0 z-10 bg-white border-b border-[#0A0A0A] px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#0A0A0A] text-white flex items-center justify-center">
              <FileText size={18} strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-xl font-extrabold tracking-tighter leading-none">
                EZBIZ INVOICE
              </h1>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] mt-1">
                Daily sales · Generate · Print · PDF
              </p>
            </div>
          </div>
          <div
            className="hidden md:flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-[#52525B]"
            data-testid="offline-badge"
          >
            <span
              className={`w-2 h-2 rounded-full ${dbReady ? (dbDirty ? "bg-[#FFA500]" : "bg-[#16a34a]") : "bg-[#FF3B30]"}`}
            />
            <span data-testid="db-status">
              {!dbReady
                ? "No database"
                : dbDirty
                  ? "Unsaved changes"
                  : `DB: ${dbName || "in-memory"}`}
            </span>
            <button
              type="button"
              onClick={() => setDbOnboardingOpen(true)}
              data-testid="db-connect-btn"
              title="Choose / change the database file"
              className="inline-flex items-center gap-1.5 border border-[#0A0A0A] px-2.5 py-1.5 hover:bg-[#0A0A0A] hover:text-white transition-colors"
            >
              <Database size={11} strokeWidth={2.5} />
              <span className="font-bold tracking-widest">DB</span>
            </button>
            {dbReady && !dbHandle && (
              <button
                type="button"
                onClick={handleSaveDbFallback}
                data-testid="db-save-fallback-btn"
                title="Download the database file"
                className="inline-flex items-center gap-1.5 border border-[#002FA7] bg-[#002FA7] text-white px-2.5 py-1.5 hover:bg-[#00227A] transition-colors"
              >
                <Save size={11} strokeWidth={2.5} />
                <span className="font-bold tracking-widest">Save .db</span>
              </button>
            )}
            {dbHandle && dbDirty && (
              <button
                type="button"
                onClick={handleSaveDbFallback}
                data-testid="db-flush-btn"
                title="Force save the database file"
                className="inline-flex items-center gap-1.5 border border-[#FFA500] bg-[#FFA500] text-[#0A0A0A] px-2.5 py-1.5 hover:bg-[#FFB733] transition-colors"
              >
                <Save size={11} strokeWidth={2.5} />
                <span className="font-bold tracking-widest">Flush</span>
              </button>
            )}
            {!installed && (
              <button
                type="button"
                onClick={handleInstall}
                data-testid="install-app-btn"
                title={
                  installable
                    ? "Install Ezbiz Invoice as a desktop / mobile app"
                    : "Open in Chrome / Edge to enable install. On iOS Safari: Share → Add to Home Screen."
                }
                className={`inline-flex items-center gap-1.5 border px-2.5 py-1.5 transition-colors ${
                  installable
                    ? "border-[#002FA7] bg-[#002FA7] text-white hover:bg-[#00227A]"
                    : "border-[#0A0A0A] bg-white text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                }`}
              >
                <MonitorDown size={11} strokeWidth={2.5} />
                <span className="font-bold tracking-widest">
                  {installable ? "Install" : "Install App"}
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setSetupOpen(true)}
              data-testid="open-setup-btn"
              title="Edit company setup"
              className="inline-flex items-center gap-1.5 border border-[#0A0A0A] px-2.5 py-1.5 hover:bg-[#0A0A0A] hover:text-white transition-colors"
            >
              <Settings2 size={11} strokeWidth={2.5} />
              <span className="font-bold tracking-widest">Setup</span>
            </button>
            <button
              type="button"
              onClick={() => setInvoicesListOpen(true)}
              data-testid="open-invoices-btn"
              title="View saved invoices"
              className="inline-flex items-center gap-1.5 border border-[#0A0A0A] px-2.5 py-1.5 hover:bg-[#0A0A0A] hover:text-white transition-colors"
            >
              <History size={11} strokeWidth={2.5} />
              <span className="font-bold tracking-widest">
                Invoices [{savedInvoices.length}]
              </span>
            </button>
            <label
              data-testid="import-config-btn"
              className="ml-2 inline-flex items-center gap-1.5 border border-[#0A0A0A] px-2.5 py-1.5 cursor-pointer hover:bg-[#0A0A0A] hover:text-white transition-colors"
              title="Import company config (.json)"
            >
              <Upload size={11} strokeWidth={2.5} />
              <span className="font-bold tracking-widest">Import</span>
              <input
                type="file"
                accept="application/json"
                className="hidden"
                data-testid="import-config-input"
                onChange={(e) => {
                  handleImportConfig(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </label>
            <button
              type="button"
              onClick={handleExportConfig}
              data-testid="export-config-btn"
              title="Export company config as JSON"
              className="inline-flex items-center gap-1.5 border border-[#0A0A0A] px-2.5 py-1.5 hover:bg-[#0A0A0A] hover:text-white transition-colors"
            >
              <Save size={11} strokeWidth={2.5} />
              <span className="font-bold tracking-widest">Export</span>
            </button>
          </div>
        </header>

        <ControlPanel
          business={business}
          customer={customer}
          setCustomer={setCustomer}
          shipping={shipping}
          setShipping={setShipping}
          eway={eway}
          setEway={setEway}
          meta={meta}
          setMeta={setMeta}
          items={items}
          setItems={setItems}
          taxRate={taxRate}
          setTaxRate={setTaxRate}
          taxLabel={taxLabel}
          setTaxLabel={setTaxLabel}
          parties={parties}
          onSelectParty={handleSelectParty}
          onSaveParty={handleSaveParty}
          onRemoveParty={handleRemoveParty}
          onImportParties={handleImportParties}
          onExportParties={handleExportParties}
          lastInvoiceNumber={lastInvoiceNumber}
        />

        <div className="sticky bottom-0 bg-white border-t border-[#0A0A0A] px-6 lg:px-10 py-4 flex flex-wrap gap-3 items-center justify-between">
          <button
            onClick={handleReset}
            data-testid="reset-btn"
            className="inline-flex items-center gap-2 border border-[#0A0A0A] bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors"
          >
            <RotateCcw size={14} strokeWidth={2.5} />
            Reset
          </button>
          <div className="flex gap-3">
            <button
              onClick={handleSaveInvoice}
              data-testid="save-invoice-btn"
              title="Save this invoice locally for later editing / reprint"
              className="inline-flex items-center gap-2 border border-[#0A0A0A] bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors"
            >
              <Save size={14} strokeWidth={2.5} />
              Save
            </button>
            <button
              onClick={handlePrint}
              data-testid="print-invoice-btn"
              className="inline-flex items-center gap-2 border border-[#0A0A0A] bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors"
            >
              <Printer size={14} strokeWidth={2.5} />
              Print
            </button>
            <button
              onClick={handleDownloadPDF}
              data-testid="download-pdf-btn"
              className="inline-flex items-center gap-2 bg-[#002FA7] px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#00227A] transition-all hover:-translate-y-px"
            >
              <Download size={14} strokeWidth={2.5} />
              Download PDF
            </button>
          </div>
        </div>
      </aside>

      {/* RIGHT — A4 Preview */}
      <main
        className="preview-stage w-full lg:w-[54%] lg:h-screen lg:overflow-y-auto scroll-clean p-6 lg:p-10 bg-[#FAFAFA] flex justify-center items-start"
        data-testid="preview-stage"
      >
        <InvoicePreview
          ref={previewRef}
          business={business}
          customer={customer}
          shipping={effectiveShipping}
          eway={eway}
          meta={meta}
          items={items}
          taxRate={taxRate}
          taxLabel={taxLabel}
          totals={totals}
        />
      </main>

      <SetupWizard
        open={setupOpen}
        onOpenChange={setSetupOpen}
        initial={{
          name: business.name,
          address: business.address,
          phone: business.phone,
          email: business.email,
          gstin: business.gstin,
          logoDataUrl: business.logoDataUrl,
          tagline: business.tagline,
          defaultUnit: business.defaultUnit,
          notes: business.notes,
          paymentDetails: business.paymentDetails,
          terms: business.terms,
          taxLabel,
          taxRate,
        }}
        onSave={handleSetupSave}
      />
      <SavedInvoicesList
        open={invoicesListOpen}
        onOpenChange={setInvoicesListOpen}
        invoices={savedInvoices}
        onLoad={handleLoadInvoice}
        onDelete={handleDeleteInvoice}
      />
      <DbOnboarding
        open={dbOnboardingOpen}
        onOpenChange={setDbOnboardingOpen}
        onCreateNew={handleCreateNewDb}
        onOpenExisting={handleOpenExistingDb}
        onImportLegacy={handleContinueWithoutFile}
      />
      <InstallGuide
        open={installGuideOpen}
        onOpenChange={setInstallGuideOpen}
        installable={installable}
      />
    </div>
  );
}
